import { AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, EventEmitter, HostListener, inject, Input, OnInit, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { ResizeModel } from '../../../model/eng/resize.model';
import { SharedService } from '../../shared/shared.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FirmModel } from '../../../model/firm/firm.model';
import { FirmService } from '../../../services/firm.service';
import { BillingModel } from '../../../model/billing/billing.model';
import { AclService } from '../../../services/acl.service';
import { BillingService } from '../../../services/billing.service';
import { AclModel } from '../../../model/acl.model';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ResizableDrawerDirective } from '../../../directives/resizable.directive';
import {MatListModule} from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import {MatExpansionModule} from '@angular/material/expansion';
import { UserProfileComponent } from '../profile/profile.component';
import { ComponentsComponent } from '../components/components.component';
import { UserNotificationsComponent } from '../notifications/notifications.component';
import { UserTeamAccessComponent } from '../teamaccess/teamaccess.component';
import { PrivacySecurityComponent } from '../privacy-security/privacy-security.component';
import { UserExportDataComponent } from '../export-data/export-data.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { SettingsOverlayInput } from '../../../model/settings-overlay-input.model';
import { UserDetailModel } from 'countable@model';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-settings-overlay',
  templateUrl: './settings-overlay.component.html',
  styleUrl: './settings-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatSidenavModule, ResizableDrawerDirective, MatButtonModule, MatListModule, MatExpansionModule, UserProfileComponent, ComponentsComponent, 
    UserNotificationsComponent, UserTeamAccessComponent, PrivacySecurityComponent, UserExportDataComponent, NgSelectModule
  ],
  standalone: true
})
export class SettingsOverlayComponent implements OnInit, AfterViewInit {

  @ViewChild('drawer') drawer!: MatDrawer;
  @ViewChild('leftPanel', { static: false }) leftPanel!: ElementRef;
  @ViewChild('centerSection', { static: false }) centerSection!: ElementRef;
  @ViewChild('togglePanel', { static: false }) togglePanel!: ElementRef;
  @ViewChild('fullOverlayWrapper', { static: false }) fullOverlayWrapper!: ElementRef;

  @Input({ required: true }) inputSettings!: WritableSignal<SettingsOverlayInput>;  

  @Output() closeOverlay = new EventEmitter<void>();

  mfaMandatory: WritableSignal<boolean> = signal<boolean>(false);
  isChangePassword: WritableSignal<boolean> = signal<boolean>(false);
  activeSection: WritableSignal<string> = signal<string>('myAccount');

  firm: FirmModel;
  billing: BillingModel;
  acl: AclModel;
  tabIndex: number = 0;
  private destroyRef = inject(DestroyRef);
  private shouldDetectOutsideClick = false;
  userDetail: UserDetailModel;

  @HostListener('document:click', ['$event.target'])
  @HostListener('document:touchstart', ['$event.target'])
  onClickOutside(target: HTMLElement): void {
      const isInside = this.centerSection.nativeElement.contains(target);
      const isLeftPanel = this.leftPanel.nativeElement.contains(target);
      const isTogglePanel = this.togglePanel.nativeElement.contains(target);
      if (window.innerWidth < 991 && isInside && !isLeftPanel && !isTogglePanel) {
          this.drawer.close();
      }
      this.checkAndCloseOverlayOnOutsideClick(target);
  }

  constructor(private sharedService: SharedService, private firmService: FirmService,
    private billingService: BillingService, private aclService: AclService, private authService: AuthService) {
      effect(() => {
      this.tabIndex = this.inputSettings().tabIndex;
    });
  }

  ngOnInit(): void {

    this.sharedService.mandatorymfa.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.mfaMandatory.set(res);
    });
    this.firmService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(firm => {
      if (firm) {
        this.firm = firm;
      }
    });
    this.billingService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(billing => {
      if (billing) {
        this.billing = billing;
      }
    });
    this.aclService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(acl => {
      this.acl = acl;
    });
    this.sharedService.isUserSettingsChangePassword.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res == 'show') {
        this.isChangePassword.set(true);
      }
      if (res == 'hide') {
        this.isChangePassword.set(false);
      }
    });
    this.sharedService.openSettingsOverlay$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: string) => {
      if(data){
        this.activeSection.set(data);
      }
    });
    this.authService.subjectUserDetail.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(detail => {
      this.userDetail = detail;
    });
  }

  ngAfterViewInit(): void {
  setTimeout(() => {
    this.shouldDetectOutsideClick = true;
  });
}

  closeWindow() {
    this.closeOverlay.emit();
  }
  
  onOverlayChange(updated: SettingsOverlayInput) {
    this.inputSettings.set(updated);
    this.closeWindow();
  }

  public onResizePanelEnd(resizeData: ResizeModel) {
    if (resizeData.minWidth !== undefined && resizeData.width < resizeData.minWidth) {
      this.drawer.close();
    }
  }

  private checkAndCloseOverlayOnOutsideClick(target: HTMLElement): void {
    if (!this.shouldDetectOutsideClick) return;

    if (!this.centerSection || !this.leftPanel || !this.togglePanel) return;

    const isInsideCenter = this.centerSection.nativeElement.contains(target);
    const isInsideLeft = this.leftPanel.nativeElement.contains(target);
    const isInsideToggle = this.togglePanel.nativeElement.contains(target);
    const isInsideOverlay =  false;
    const isDialog = !!target.closest('mat-dialog-container,  .custom-tooltip, .custom-dialog, .cdk-overlay-container');
    

    if (!isInsideCenter && !isInsideLeft && !isInsideToggle && !isDialog && !isInsideOverlay) {
      this.closeOverlay.emit();
    }
  }

}
